(ns app.front.view.group.admin.fees.variable.variable-events
  (:require [app.domain.fee.model :as fee-model]
            [app.domain.fee.validate :as validate]
            [app.front.entities :as entities]
            [app.front.view.group.admin.fees.formula.formula-events :as ff-events]
            [app.front.view.group.admin.fees.template.template-events :as ft-events]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-event-db ::select-fee-variable
  (fn [db [_ fee-variable-id]]
    (-> db
        (p/dissoc-in [::fee-variables :selected-form])
        (assoc-in [::fee-variables :selected-fee-variable] fee-variable-id))))


(defn title->identifier [title]
  (when title
    (as-> title t
          (str/trim t)
          (str/lower-case t)
          (str/replace t #"\s" "_")
          (str ":" t))))


(defn select-variable-fields
  "Filters out irrelevant key-value pairs before request submission.

  Without filtering, entity-db saves all keys when backend returns 200,
  causing variable state inconsistency."
  [payload]
  (if-let [source-type (:fee-variable/source-type payload)]
    (select-keys payload
                 [:fee-variable/titles
                  :fee-variable/identifier
                  :fee-variable/source-type
                  (case source-type
                    :fee-variable.source-type/fee :fee-variable/fee-template
                    :fee-variable.source-type/formula :fee-variable/fee-formula
                    :fee-variable.source-type/form :fee-variable/source-field)])
    payload))


(rf/reg-event-db ::change-field
  (fn [db [_ fee-variable-id field value]]
    (assoc-in db [::fee-variables :unsaved-changes fee-variable-id field] value)))


(rf/reg-event-db ::change-title
  (fn [db [_ fee-variable-id variable-used? value]]
    (let [db-with-title-change (assoc-in db [::fee-variables :unsaved-changes fee-variable-id :fee-variable/titles] value)]
      (if variable-used?
        db-with-title-change
        (-> db-with-title-change
            (assoc-in [::fee-variables :unsaved-changes fee-variable-id :fee-variable/identifier]
                      (title->identifier (i18n-model/get-text-from-entities-or-map value i18n-model/default-lang))))))))


(rf/reg-event-db ::select-form
  (fn [db [_ form]]
    (assoc-in db [::fee-variables :selected-form] form)))


(rf/reg-event-fx ::change-formula
  (fn [{:keys [db]} [_ fee-variable-id value]]
    {:db                (assoc-in db [::fee-variables :unsaved-changes fee-variable-id :fee-variable/fee-formula] value)
     :dispatch          [::validate-cycle fee-variable-id :fee-variable/fee-formula value]
     :dispatch-debounce {:id      (str ::formula-validation "-" fee-variable-id)
                         :timeout 1000
                         :action  :dispatch
                         :event   [::validate-formula fee-variable-id value]}}))


(rf/reg-event-fx ::validate-cycle
  (fn [{:keys [db]} [_ fee-variable-id field value]]
    (let [fee-schedule  (get-in db [::ft-events/fee-templates :fee-schedule])
          fee-templates (fee-model/schedule-fee-templates fee-schedule)
          fee-variables (fee-model/schedule-fee-variables fee-schedule)
          cycle-result  (validate/fee-cycle? fee-templates fee-variables fee-variable-id {field value})
          request-id    (str "formula-dependency-cycle-" fee-variable-id)]
      (if cycle-result
        (let [error (validate/humanize-cycle-error cycle-result fee-templates)]
          {:db       (assoc-in db [::fee-variables :errors fee-variable-id :cycle] error)
           :dispatch [:notifications/upsert
                      :toast
                      :error
                      :error/error
                      (map vector error)
                      request-id]})
        {:db       (update-in db [::fee-variables :errors fee-variable-id] dissoc :cycle)
         :dispatch [:notifications/remove request-id]}))))


(rf/reg-event-fx ::validate-formula
  (fn [{:keys [db]} [_ fee-variable-id formula]]
    {:db           (assoc-in db [::fee-variables :formula-status fee-variable-id] :loading)
     :comm/command {:command    :fee/validate-formula
                    :method     :post
                    :data       {:formula formula}
                    :on-success [::formula-validated fee-variable-id]}}))


(rf/reg-event-db ::formula-validated
  (fn [db [_ fee-variable-id _ response]]
    (let [status (if (:valid? response) :valid :invalid)]
      (-> db
          (assoc-in [::fee-variables :formula-status fee-variable-id] status)
          (assoc-in [::fee-variables :formula-message fee-variable-id] (:message response))))))


(rf/reg-event-fx ::save
  (fn [{:keys [db]} [_ fee-variable-id]]
    (let [id              (get-in db [::fee-variables :selected-fee-variable])
          fee-schedule-id (-> db ::ft-events/fee-templates :fee-schedule :db/id)
          creating-new?   (= id :new)
          raw-payload     (get-in db [::fee-variables :unsaved-changes fee-variable-id])
          payload         (select-variable-fields raw-payload)]
      (if creating-new?
        {:comm/command {:command    :fee/create-fee-variable
                        :method     :post
                        :data       {:fee-schedule-id fee-schedule-id
                                     :data            payload}
                        :on-success [::create-success]}}
        {:comm/command {:command    :fee/edit-fee-variable
                        :method     :post
                        :data       {:fee-variable-id fee-variable-id
                                     :changes         payload}
                        :on-success [::save-success]}}))))


(rf/reg-event-db ::cancel
  (fn [db _]
    (let [creating-new? (-> db
                            ::fee-variables
                            :selected-fee-variable
                            (= :new))]
      (if creating-new?
        (-> db
            (p/dissoc-in [::fee-variables :selected-fee-variable])
            (p/dissoc-in [::fee-variables :unsaved-changes])
            (p/dissoc-in [::ff-events/formula :test :new]))
        (p/dissoc-in db [::fee-variables :unsaved-changes])))))


(rf/reg-event-fx ::delete
  (fn [_ [_ fee-variable-id]]
    {:comm/command {:command    :fee/delete-fee-variable
                    :method     :post
                    :data       {:fee-variable-id fee-variable-id}
                    :on-success [::delete-success]}}))


(rf/reg-event-fx ::create-success
  (fn [{:keys [db]} [_ status changes]]
    (let [created-variable-id (some (fn [[entity-id {type :entity/type}]]
                                      (when (= type [:assoc :entity.type/fee-variable])
                                        entity-id))
                                    changes)]
      {:db       (-> db
                     (p/dissoc-in [::fee-variables :unsaved-changes])
                     (assoc-in [::fee-variables :selected-fee-variable] created-variable-id))
       :dispatch [::entities/apply-changes status changes]})))


(rf/reg-event-fx ::save-success
  (fn [{:keys [db]} [_ status changes]]
    {:db         (p/dissoc-in db [::fee-variables :unsaved-changes])
     :dispatch-n [[::entities/apply-changes status changes]
                  [:notifications/add :toast :success :success :changes-saved]]}))


(rf/reg-event-fx ::delete-success
  (fn [{:keys [db]} [_ status changes]]
    {:dispatch [::entities/apply-changes status changes]
     :db       (-> db
                   (p/dissoc-in [::fee-variables :selected-fee-variable])
                   (p/dissoc-in [::fee-variables :unsaved-changes]))}))


(rf/reg-event-db ::create-new-fee-variable
  (fn [db _]
    (-> db
        (assoc-in [::fee-variables :selected-fee-variable] :new)
        (assoc-in [::fee-variables :unsaved-changes :new :fee-variable/source-type] :fee-variable.source-type/form))))
